import { useEffect, useState } from 'react';
import shippingService from '../../api/services/shipping.service';
import OrderTracking from '../OrderTracking/OrderTracking';
import { normalizeImageUrl } from '../../utils/imageUtils';
import './OrderDetailsPage.css';

const toNumber = (val, fallback = 0) => {
  const num = Number(val);
  return Number.isFinite(num) ? num : fallback;
};

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(toNumber(val));

const getDurationLabel = (days) => {
  const d = toNumber(days);
  if (d === 0) return '';
  if (d % 30 === 0) {
    const months = d / 30;
    return `${months} ${months === 1 ? 'month' : 'months'}`;
  }
  if (d % 7 === 0) {
    const weeks = d / 7;
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  }
  return `${d} days`;
};

const formatPurchaseType = (value) => {
  const type = String(value || '').toLowerCase();
  if (type === 'one_time' || type === 'onetime') return 'One-time';
  if (type === 'subscription') return 'Subscription';
  if (!type) return 'One-time';
  return type.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getUnitPrice = (item) => item?.unit_price ?? item?.variant_price ?? item?.price ?? 0;

const getItemTotal = (item) =>
  item?.price ??
  item?.item_total ??
  item?.total ??
  item?.subtotal ??
  toNumber(getUnitPrice(item)) * toNumber(item?.quantity, 1) * toNumber(item?.multiplier, 1);

const getTotalAmount = (order) =>
  order?.total_amount ??
  order?.total ??
  (Array.isArray(order?.details)
    ? order.details.reduce((sum, it) => sum + toNumber(getItemTotal(it)), 0)
    : 0);

const getOrderId = (order) =>
  order?.order_uuid || order?.order_number || order?.uuid || order?.id || 'N/A';
const getOrderDate = (order) => order?.createdAt || order?.created_at || order?.created_on;
const getOrderStatus = (order) => order?.order_status || order?.status || 'N/A';

const getItemImageUrl = (item) => {
  const candidates = [
    item?.product_image,
    item?.image,
    item?.image_url,
    item?.product?.image,
    item?.product?.product_image,
    item?.product?.images,
    item?.images,
  ];

  for (const imageData of candidates) {
    if (!imageData) continue;

    let imageUrl = '';
    if (typeof imageData === 'string') imageUrl = imageData;
    else if (Array.isArray(imageData) && imageData.length > 0) imageUrl = imageData[0];
    else if (typeof imageData === 'object') {
      imageUrl = imageData.url || imageData.src || imageData.image || imageData[0];
    }

    if (imageUrl) {
      const normalized = normalizeImageUrl(imageUrl);
      if (normalized) return normalized;
    }
  }

  return '/product-placeholder.png';
};

const formatAddress = (address) => {
  if (!address) return null;
  return {
    name: address.name || '',
    lines: [address.street1, address.street2].filter(Boolean),
    cityLine: [address.city, address.state, address.zip].filter(Boolean).join(', '),
  };
};

const formatDeliveryDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
};

export default function OrderDetailsPage({ order, onBack }) {
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');

  const orderUuid = getOrderId(order);
  const status = String(getOrderStatus(order)).toLowerCase();
  const isCancelled = status.includes('cancel');

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!orderUuid || orderUuid === 'N/A' || isCancelled) {
        setTrackingData(null);
        return;
      }

      setTrackingLoading(true);
      setTrackingError('');

      try {
        const data = await shippingService.getTrackingByOrder(orderUuid);
        if (isMounted) setTrackingData(data);
      } catch (err) {
        if (isMounted) {
          setTrackingData(null);
          setTrackingError(err.message || 'Unable to load tracking details right now.');
        }
      } finally {
        if (isMounted) setTrackingLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [orderUuid, isCancelled]);

  if (!order) return null;

  const items = order.details || [];
  const from = formatAddress(trackingData?.address_from);
  const to = formatAddress(trackingData?.address_to);
  const rate = trackingData?.selected_rate;
  const serviceName = rate?.servicelevel?.name || trackingData?.servicelevel?.name || '';
  const carrier = String(trackingData?.carrier || rate?.provider || '').toUpperCase();
  const eta = formatDeliveryDate(trackingData?.eta);

  const orderTax = (() => {
    const orderLevel = order.tax ?? 0;
    const itemsTax = items.reduce((sum, item) => sum + toNumber(item.tax_amount), 0);
    return orderLevel > 0 ? orderLevel : itemsTax;
  })();

  const heroHeadline = isCancelled
    ? 'This order was cancelled'
    : eta
      ? `Arriving ${eta}`
      : trackingData?.tracking_number
        ? 'Your order is on its way'
        : 'We are preparing your order';

  const heroSub = isCancelled
    ? 'Any payment taken will be refunded to your original payment method.'
    : trackingData?.tracking_number
      ? `${carrier || 'Carrier'}${serviceName ? ` · ${serviceName}` : ''}`
      : 'You will get tracking details as soon as it ships.';

  return (
    <div className="order-page">
      <button type="button" className="order-page-back" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
        Back to my orders
      </button>

      {/* Hero - status, delivery date, order identity */}
      <section className={`order-hero${isCancelled ? ' is-cancelled' : ''}`}>
        <div className="order-hero-top">
          <span className="order-hero-id">{orderUuid}</span>
          <span className={`status-badge ${status}`}>{String(getOrderStatus(order)).toUpperCase()}</span>
        </div>
        <h1 className="order-hero-headline">{heroHeadline}</h1>
        <p className="order-hero-sub">{heroSub}</p>
        <p className="order-hero-meta">
          Placed{' '}
          {getOrderDate(order)
            ? new Date(getOrderDate(order)).toLocaleDateString([], {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })
            : 'N/A'}
          {' · '}
          {items.length} {items.length === 1 ? 'item' : 'items'}
          {' · '}
          {formatCurrency(getTotalAmount(order))}
        </p>
      </section>

      <div className="order-page-sections">
          {/* Tracking and route are one delivery story - not two cards */}
          {(!isCancelled || from || to) && (
            <section className="order-panel order-delivery-panel">
              {!isCancelled && (
                <OrderTracking
                  order={order}
                  trackingData={trackingData}
                  loading={trackingLoading}
                  error={trackingError}
                />
              )}

              {(from || to) && (
                <>
                  <h3 className="order-subhead">Shipment route</h3>
                  <div className="order-route">
                {from && (
                  <div className="order-route-node">
                    <span className="order-route-label">From</span>
                    <span className="order-route-name">{from.name}</span>
                    {from.lines.map((line) => (
                      <span key={line} className="order-route-line">{line}</span>
                    ))}
                    <span className="order-route-line">{from.cityLine}</span>
                  </div>
                )}

                <div className="order-route-arrow" aria-hidden="true">
                  <span className="order-route-dash" />
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="15" height="13" rx="2" />
                    <path d="M16 8h4l3 3v5h-7z" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                  <span className="order-route-dash" />
                </div>

                {to && (
                  <div className="order-route-node is-destination">
                    <span className="order-route-label">To</span>
                    <span className="order-route-name">{to.name}</span>
                    {to.lines.map((line) => (
                      <span key={line} className="order-route-line">{line}</span>
                    ))}
                    <span className="order-route-line">{to.cityLine || 'Address on file'}</span>
                  </div>
                )}
                  </div>
                </>
              )}
            </section>
          )}

          <section className="order-panel">
            <h2 className="order-panel-title">
              Items <span className="order-panel-count">{items.length}</span>
            </h2>
            <div className="order-items">
              {items.map((item, index) => {
                const multiplier = toNumber(item?.multiplier, 1);
                const unitPrice = toNumber(getUnitPrice(item));
                const quantity = toNumber(item?.quantity, 1);

                const linePrice = toNumber(item?.price, unitPrice * quantity * multiplier);
                const tax = toNumber(item?.tax_amount);
                const discountPct = toNumber(item?.discount_percentage);
                const discountPerUnit = toNumber(item?.discount_price);

                return (
                  <div key={index} className="order-item-row">
                    <img
                      src={getItemImageUrl(item)}
                      alt={item?.product_name || 'Product'}
                      className="order-item-image"
                      onError={(e) => {
                        e.target.src = '/product-placeholder.png';
                      }}
                    />
                    <div className="order-item-body">
                      <div className="order-item-head">
                        <p className="order-item-name">{item?.product_name || 'Item'}</p>
                        <span className="order-item-price">
                          {formatCurrency(getItemTotal(item))}
                        </span>
                      </div>
                      {item?.variant_name && (
                        <p className="order-item-variant">{item.variant_name}</p>
                      )}

                      <div className="order-item-meta">
                        <div className="order-item-meta-cell">
                          <label>Unit price</label>
                          <span>
                            {formatCurrency(unitPrice)}
                            {discountPct > 0 && (
                              <span className="order-was-price">
                                {formatCurrency(item.variant_price)}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="order-item-meta-cell">
                          <label>Quantity</label>
                          <span>{quantity}</span>
                        </div>
                        <div className="order-item-meta-cell">
                          <label>Purchase type</label>
                          <span>{formatPurchaseType(item?.purchase_type)}</span>
                        </div>
                      </div>

                      {/* Unit price x quantity rarely equals the total - the
                          billing cycles and tax have to be visible */}
                      <div className="order-line-breakdown">
                        {discountPct > 0 && (
                          <div className="order-line-row is-discount">
                            <span>You saved</span>
                            <span>
                              {discountPct}% off · −{formatCurrency(discountPerUnit)} per unit
                            </span>
                          </div>
                        )}

                        {multiplier > 1 && (
                          <div className="order-line-row">
                            <span>Billing</span>
                            <span>
                              {getDurationLabel(item.billing_duration)} plan · {multiplier} cycles
                            </span>
                          </div>
                        )}

                        <div className="order-line-row">
                          <span>Item subtotal</span>
                          <span>
                            <span className="order-calc-hint">
                              {formatCurrency(unitPrice)} × {quantity}
                              {multiplier > 1 ? ` × ${multiplier}` : ''} =
                            </span>{' '}
                            {formatCurrency(linePrice)}
                          </span>
                        </div>

                        <div className="order-line-row">
                          <span>Tax</span>
                          <span>{tax > 0 ? formatCurrency(tax) : 'No tax'}</span>
                        </div>

                        <div className="order-line-row is-total">
                          <span>Item total</span>
                          <span>{formatCurrency(getItemTotal(item))}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

        <section className="order-panel">
          <h2 className="order-panel-title">Order summary</h2>
          <div className="order-summary-grid">
            <div className="order-summary-col">
              <h3 className="order-subhead">Charges</h3>
              <div className="order-summary-rows">
              <div className="order-summary-row">
                <span>Subtotal</span>
                <span>{formatCurrency(order.amount)}</span>
              </div>
              <div className="order-summary-row">
                <span>Shipping</span>
                <span>{formatCurrency(order.shipping_price)}</span>
              </div>
              {orderTax > 0 && (
                <div className="order-summary-row">
                  <span>Tax</span>
                  <span>{formatCurrency(orderTax)}</span>
                </div>
              )}
              {order.discount_amount > 0 && (
                <div className="order-summary-row is-discount">
                  <span>Discount</span>
                  <span>-{formatCurrency(order.discount_amount)}</span>
                </div>
              )}
              <div className="order-summary-row is-total">
                <span>Total</span>
                <span>{formatCurrency(getTotalAmount(order))}</span>
              </div>
              </div>
            </div>

            {(order.payment_gateway || order.transaction_uuid || order.payment_status) && (
              <div className="order-summary-col">
                <h3 className="order-subhead">Payment</h3>
                <div className="order-inline-rows">
                  {order.payment_gateway && (
                    <div className="order-inline-row">
                      <span>Method</span>
                      <span>{String(order.payment_gateway).toUpperCase()}</span>
                    </div>
                  )}
                  {order.transaction_uuid && (
                    <div className="order-inline-row">
                      <span>Transaction</span>
                      <span className="order-mono">{order.transaction_uuid}</span>
                    </div>
                  )}
                  {order.payment_status && (
                    <div className="order-inline-row">
                      <span>Status</span>
                      <span className={`order-pay-status ${String(order.payment_status).toLowerCase()}`}>
                        {String(order.payment_status).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {order.shipping_address && (
              <div className="order-summary-col">
                <h3 className="order-subhead">Delivery address</h3>
                <div className="order-address">
                  <p className="order-address-name">{order.shipping_address.name}</p>
                  <p>
                    {[order.shipping_address.apartment_number, order.shipping_address.address]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  <p>
                    {[
                      order.shipping_address.city,
                      order.shipping_address.state,
                      order.shipping_address.pincode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {order.shipping_address.mobile && <p>{order.shipping_address.mobile}</p>}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="order-page-help">
        <span>Need help with this order?</span>
        <a href="mailto:Support@chilltreebotanicals.com" className="order-page-help-link">
          Contact support
        </a>
      </div>
    </div>
  );
}
